import React, { useEffect, useRef, useState } from "react";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { useAppSelector } from "../../../store";
import { useTranslation } from "react-i18next";
import InnerInput from "../../../components/inputs/InnerInput/InnerInput";
import SwitchBtn from "../../../components/buttons/SwitchBtn";
import TextEditor from "../../../components/wysiwyg/Wysiwyg";
import instance from "../../../api/apiClient";
import Loader from "../../../components/global/Loader";
import { ILocaleData } from "../../../configs/types";
import Button from "../../../components/buttons/Button";
import { apiRoutes } from "../../../configs/apiRoutes";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { capitalize } from "helpers/capitalize";

interface Props {
  id?: string;
  currentData: any;
  handleData: (e: ILocaleData) => void;
  url: string;
  type?: string;
  title?: string;
  isAiAssist?: boolean;
  fieldErrors?: Record<string, string>;
  clearFieldError?: (field: string) => void;
  resetErrors?: () => void;
  readOnly?: boolean;
  domainOptions?: { value: string; label: string }[];
  onReadyChange?: (ready: boolean) => void;
  onInitialData?: (data: ILocaleData) => void;
  onLoadError?: (error: any) => void;
  titleOnly?: boolean;
}
const LocalTab: React.FC<Props> = ({
  id,
  handleData,
  currentData,
  url,
  isAiAssist,
  type,
  title,
  fieldErrors,
  clearFieldError,
  resetErrors,
  readOnly,
  domainOptions,
  onReadyChange,
  onInitialData,
  onLoadError,
  titleOnly = false,
}) => {
  const localesList = useAppSelector((store) => store.settings.locales);
  const geoList = useAppSelector(selectFilteredGeo);
  const { t } = useTranslation();

  const [activeGeo, setActiveGeo] = useState<string>();
  const [activeLocale, setActiveLocale] = useState<string>();
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [isFetchedTranslate, setIsFetchedTranslate] = useState<boolean>(true);
  const [activeDomain, setActiveDomain] = useState("");
  const hasDomains = Boolean(domainOptions);
  const requestVersion = useRef(0);
  const callbacks = useRef({ handleData, onReadyChange, onLoadError });
  callbacks.current = { handleData, onReadyChange, onLoadError };

  const invalidateTranslation = () => {
    if (!hasDomains) return;
    requestVersion.current += 1;
    setIsFetched(false);
    setIsFetchedTranslate(true);
    onReadyChange?.(false);
  };

  useEffect(() => {
    if (geoList.length && localesList.length) {
      setActiveGeo(geoList[0]);
      setActiveLocale(localesList[0]);

      if (!hasDomains)
        handleData({
          ...(currentData || {}),
          geo: geoList[0],
          locale: localesList[0],
        });
    }
  }, [geoList, localesList]);

  useEffect(() => {
    if (id && !hasDomains) {
      handleGet();
    }
  }, [id, activeGeo, activeLocale, hasDomains]);

  useEffect(() => {
    if (!hasDomains || !id || !activeGeo || !activeLocale) return;
    const version = ++requestVersion.current;
    const selection = {
      geo: activeGeo,
      locale: activeLocale,
      ...(activeDomain ? { domain: activeDomain } : {}),
    };
    const empty = { ...selection, title: "", content: "", isActive: false };
    setIsFetched(false);
    callbacks.current.onReadyChange?.(false);
    callbacks.current.handleData(empty);
    instance
      .get(`${url}/${id}`, { params: selection })
      .then((result) => {
        if (version !== requestVersion.current) return;
        const translation = {
          ...empty,
          title: result.data?.title ?? "",
          content: result.data?.content ?? "",
          isActive: Boolean(result.data?.isActive),
        };
        callbacks.current.handleData(translation);
        onInitialData?.(translation);
        callbacks.current.onReadyChange?.(true);
      })
      .catch((error) => {
        if (version !== requestVersion.current) return;
        if (error.status === 404) {
          callbacks.current.onReadyChange?.(true);
        } else {
          callbacks.current.onLoadError?.(error);
        }
      })
      .finally(() => {
        if (version === requestVersion.current) setIsFetched(true);
      });
    return () => {
      requestVersion.current += 1;
    };
  }, [id, url, activeGeo, activeLocale, activeDomain, hasDomains]);

  useEffect(() => {
    resetErrors?.();
  }, [activeLocale, activeGeo, activeDomain]);

  const handleGet = async () => {
    if (!id || !activeGeo || !activeLocale) return;

    setIsFetched(false);
    onReadyChange?.(false);

    try {
      const result = await instance.get(
        `${url}/${id}/?geo=${activeGeo}&locale=${activeLocale}`,
      );

      handleData(result?.data);
      onInitialData?.(result?.data);
    } catch {
      console.log("handleGet error");
    }
    setIsFetched(true);
    onReadyChange?.(true);
  };

  const handleAITranslate = async () => {
    const version = requestVersion.current;
    if (id && activeGeo && activeLocale) {
      const data = {
        instanceType: type,
        instanceId: id,
        attribute: "content",
        locale: activeLocale,
        geo: activeGeo,
      };
      setIsFetchedTranslate(false);
      try {
        const result = await instance.post(
          apiRoutes.generateAITranslateText,
          data,
        );
        if (!hasDomains || version === requestVersion.current) {
          handleData({ ...currentData, content: result?.data?.text });
        }
      } catch {
        console.log("translate error");
      }
      if (!hasDomains || version === requestVersion.current)
        setIsFetchedTranslate(true);
    }
  };
  const handleAIText = async () => {
    setIsFetchedTranslate(false);

    try {
      const result = await instance.post(apiRoutes.createAIText, {
        instanceType: type,
        instanceId: -1,
        attribute: "content",
        fromField: currentData?.title ? currentData?.title : title,
        locale: activeLocale,
        geo: activeGeo,
      });
      handleData({ ...currentData, content: result?.data?.text });
    } catch {
      console.log("handleAIText error");
    }
    setIsFetchedTranslate(true);
  };

  return (
    <div className="cms-page-tab mt-14">
      <div className="game-modal-tabs small">
        {geoList.map((el) => (
          <div
            className={`tab-item ${activeGeo === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              if (activeGeo === el) return;
              invalidateTranslation();
              setActiveGeo(el);
              if (!hasDomains) handleData({ ...currentData, geo: el });
            }}
          >
            {el.toLowerCase() === "ww" ? (
              <span className="mr-5">🌎</span>
            ) : (
              <img
                src={`https://flagcdn.com/w40/${el.toLowerCase()}.png`}
                alt={el}
              />
            )}
            {t(el)}
          </div>
        ))}
      </div>

      <div className="game-modal-tabs small mt-14">
        {localesList.map((el) => (
          <div
            className={`tab-item ${activeLocale === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              if (activeLocale === el) return;
              invalidateTranslation();
              setActiveLocale(el);
              if (!hasDomains) handleData({ ...currentData, locale: el });
            }}
          >
            <img
              src={`https://flagpedia.net/data/flags/icon/16x12/${el
                .split("-")[1]
                ?.toLowerCase()}.png`}
              alt=""
            />
            {t(el).replace(/-[a-z]+$/i, "")}
          </div>
        ))}
      </div>

      <div className="pages-form-content mt-14">
        {!isFetched ? (
          <ModalTabSkeleton />
        ) : (
          <>
            <div className={`row ${hasDomains ? "page-locale-fields" : ""}`}>
              <div className="bonus-form-box--item flex-1">
                <InnerInput
                  value={currentData?.title || ""}
                  placeholder={t("title")}
                  label={t("title")}
                  type="text"
                  id="banners-title"
                  onChange={(e) => {
                    handleData({ ...currentData, title: e });
                    clearFieldError?.("title");
                  }}
                  hasError={Boolean(fieldErrors?.["title"])}
                  disabled={readOnly}
                />
              </div>

              {domainOptions && (
                <div className="bonus-form-box--item page-domain-field">
                  <DefaultDropdown
                    label="Domain"
                    menuMaxHeight={240}
                    value={activeDomain}
                    placeholder="Select domain"
                    options={domainOptions}
                    onSelect={(value) => {
                      if (value === activeDomain) return;
                      invalidateTranslation();
                      setActiveDomain(String(value));
                    }}
                  />
                </div>
              )}

              {type === "promo" && (
                <div className="bonus-form-box--item ml-20">
                  <InnerInput
                    value={currentData?.promoLabel || ""}
                    placeholder={t("label")}
                    label={capitalize(t("label"))}
                    type="text"
                    id="promo-label"
                    onChange={(e) => {
                      handleData({ ...currentData, promoLabel: e });
                      clearFieldError?.("promoLabel");
                    }}
                    hasError={Boolean(fieldErrors?.["promoLabel"])}
                    disabled={readOnly}
                  />
                </div>
              )}

              {!titleOnly && (
                <div className="bonus-form-box--item flex-1">
                  <SwitchBtn
                    activeTxt={t("active")}
                    isActive={currentData?.isActive}
                    handleClick={() =>
                      handleData({
                        ...currentData,
                        isActive: !currentData?.isActive,
                      })
                    }
                    disabled={readOnly}
                  />
                </div>
              )}
            </div>

            {!titleOnly && (
              <div className="bonus-form-box--item html-editor">
                <div className="row">
                  <div className="label">{t("content")}</div>
                  <div className="ai-buttons">
                    <Button
                      disabled={!isAiAssist || !isFetchedTranslate || readOnly}
                      handleClick={
                        type === "promo" ? handleAIText : handleAITranslate
                      }
                      txt={
                        !isFetchedTranslate ? <Loader size={8} /> : t("aiAssist")
                      }
                    />
                  </div>
                </div>

                <TextEditor
                  value={currentData?.content || ""}
                  disabled={!isFetchedTranslate || readOnly}
                  onChange={(html) => {
                    handleData({ ...currentData, content: html });
                    clearFieldError?.("content");
                  }}
                  hasError={Boolean(fieldErrors?.["content"])}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LocalTab;
